// React
import { memo } from "react";

// Helpers
import { formatCurrency } from "helpers";

// Servisni sloj
import { formatUnitPrice } from "services/api/pricing/consts";

// Tipovi
import { PricingPackSummaryProps } from "./types";

/**
 * Jedan paket u pregledu: naziv, opis, puna cijena po vozilu, najniža koju razredi donesu i
 * cijena razred po razred.
 *
 * Puna cijena se ispisuje kroz `formatUnitPrice`, dakle onako kako je upisana, do četiri
 * decimale. Izvedeni iznosi idu na dvije, jer su rezultat množenja stopom i u obračunu se
 * ionako zaokružuju na cent; bez toga bi uz „1,50 €" stajalo „1,425 €".
 *
 * Bez vlastitog okvira — kartice stoje u zajedničkoj plohi razdvojene crtom, pa bi svaka
 * svojim rubom udvostručila liniju.
 */
const PricingPackSummary: React.FC<PricingPackSummaryProps> = (
  props: PricingPackSummaryProps
) => {
  const { packNames, description, unitPrice, rows, isAnnual } = props;

  // Najniža cijena kroz sve razrede. Bez degresije je jednaka punoj, što je točan podatak.
  const lowestPerVehicle = rows.length > 0 ? Math.min(...rows.map((row) => row.perVehicle)) : null;

  return (
    <div className="w-[260px] flex-shrink-0 snap-start bg-white p-6 flex flex-col">
      {/* Fiksna visina drži cijene poravnate kad se naziv ili opis preliju u dva retka. */}
      <div className="min-h-[76px]">
        <div className="text-[15px] font-semibold text-slate-900 leading-snug">
          {packNames.length === 0 ? "Bez paketa" : packNames.join(" + ")}
        </div>

        <p className="mt-1 text-[13px] text-slate-500 leading-snug">{description}</p>
      </div>

      <div className="mt-5 flex items-baseline gap-1.5">
        <span className="text-[34px] leading-none font-bold tracking-tight text-slate-900">
          {unitPrice === null ? "—" : formatUnitPrice.format(unitPrice)}
        </span>
        <span className="text-[13px] text-slate-400">/vozilo</span>
      </div>

      <div className="mt-1.5 h-4 text-[12px] text-slate-400">
        {isAnnual ? "mjesečno uz godišnje plaćanje" : "mjesečno, puna cijena"}
      </div>

      {lowestPerVehicle !== null && (
        <div className="mt-5 rounded-lg bg-blue-50 px-3 py-2.5">
          <div className="flex items-baseline gap-1.5">
            <span className="text-[18px] font-semibold text-blue-900">
              {formatCurrency.format(lowestPerVehicle)}
            </span>
            <span className="text-[12px] text-blue-700">najniža po vozilu</span>
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <ul className="mt-5 flex-1 divide-y divide-slate-100">
          {rows.map((row) => (
            <li
              key={row.label}
              className="flex items-baseline justify-between gap-3 py-2 text-[13px]"
            >
              <span className="text-slate-500">{row.label}</span>

              <span className="flex items-baseline gap-1.5 whitespace-nowrap">
                <span className="font-semibold text-slate-900">
                  {formatCurrency.format(row.perVehicle)}
                </span>

                {/*
                  Postotak je oznaka, ne rečenica: u pilulu stane bez prelamanja, dok se
                  „· 10 % jeftinije" na uskoj kartici lomilo nasred broja.
                */}
                {row.discountPercent > 0 && (
                  <span className="rounded-full bg-green-50 px-1.5 py-0.5 text-[11px] font-medium text-green-700">
                    −{row.discountPercent} %
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default memo(PricingPackSummary);
